import React, { useState, useEffect } from 'react';
import { opportunitiesAPI, contactsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, MoreHorizontal, Pencil, Trash2, Calendar, Euro, Filter } from 'lucide-react';
import { formatCurrency, formatDate, stageLabels, truncate } from '../lib/utils';
import { toast } from 'sonner';

const STAGES = ['lead', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];

const STAGE_CONFIG = {
  lead:        { label: 'Prospect',     color: '#94A3B8', bg: 'bg-slate-100 dark:bg-slate-800/50',  border: 'border-t-slate-400' },
  qualified:   { label: 'Qualifié',     color: '#2563EB', bg: 'bg-blue-50/50 dark:bg-blue-950/20',  border: 'border-t-blue-500' },
  proposal:    { label: 'Proposition',  color: '#EA580C', bg: 'bg-orange-50/50 dark:bg-orange-950/20', border: 'border-t-orange-500' },
  negotiation: { label: 'Négociation', color: '#9333EA', bg: 'bg-purple-50/50 dark:bg-purple-950/20', border: 'border-t-purple-500' },
  won:         { label: 'Gagné',        color: '#16A34A', bg: 'bg-green-50/50 dark:bg-green-950/20',  border: 'border-t-green-500' },
  lost:        { label: 'Perdu',        color: '#DC2626', bg: 'bg-red-50/50 dark:bg-red-950/20',    border: 'border-t-red-500' },
};

const emptyOpportunity = {
  title: '', description: '', contact_id: '',
  value: 0, stage: 'lead', probability: 10, expected_close_date: '',
};

export default function OpportunitiesPage() {
  const [opportunities, setOpportunities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [formData, setFormData] = useState(emptyOpportunity);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const loadData = async () => {
    try {
      const [oppsRes, contactsRes] = await Promise.all([
        opportunitiesAPI.list(),
        contactsAPI.list(),
      ]);
      setOpportunities(oppsRes.data);
      setContacts(contactsRes.data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const getOpportunitiesByStage = (stage) => opportunities.filter(o => o.stage === stage);
  const getContactName = (id) => {
    const c = contacts.find(c => c.id === id);
    return c ? `${c.first_name} ${c.last_name}` : '—';
  };

  const openCreateDialog = (stage = 'lead') => {
    setEditingOpp(null);
    setFormData({ ...emptyOpportunity, stage });
    setDialogOpen(true);
  };

  const openEditDialog = (opp) => {
    setEditingOpp(opp);
    setFormData({
      title: opp.title,
      description: opp.description || '',
      contact_id: opp.contact_id,
      value: opp.value,
      stage: opp.stage,
      probability: opp.probability,
      expected_close_date: opp.expected_close_date ? opp.expected_close_date.split('T')[0] : '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        value: parseFloat(formData.value) || 0,
        probability: parseInt(formData.probability) || 10,
        expected_close_date: formData.expected_close_date || null,
      };
      if (editingOpp) {
        await opportunitiesAPI.update(editingOpp.id, data);
        toast.success('Opportunité mise à jour');
      } else {
        await opportunitiesAPI.create(data);
        toast.success('Opportunité créée');
      }
      setDialogOpen(false);
      loadData();
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette opportunité ?')) return;
    try {
      await opportunitiesAPI.delete(id);
      toast.success('Supprimée');
      loadData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDragStart = (e, opp) => {
    setDraggedItem(opp);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggedItem || draggedItem.stage === newStage) { setDraggedItem(null); return; }
    try {
      await opportunitiesAPI.update(draggedItem.id, { stage: newStage });
      toast.success(`Déplacé vers ${STAGE_CONFIG[newStage].label}`);
      loadData();
    } catch {
      toast.error('Erreur lors du déplacement');
    }
    setDraggedItem(null);
  };

  const [mobileStage, setMobileStage] = useState('all');

  const totalValue = opportunities.reduce((s, o) => s + Number(o.value || 0), 0);
  const activeDeals = opportunities.filter(o => !['won', 'lost'].includes(o.stage)).length;
  const wonDeals = opportunities.filter(o => o.stage === 'won');
  const wonValue = wonDeals.reduce((s, o) => s + Number(o.value || 0), 0);

  if (loading) {
    return (
      <div className="space-y-5" data-testid="opportunities-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-10 w-44" />
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map(s => <Skeleton key={s} className="w-72 h-96 flex-shrink-0" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="opportunities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Sales Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Division commerciale · Objectifs trimestriels</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2">
            <Filter size={15} />
            Filtrer
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openCreateDialog()} size="sm" className="gap-2" data-testid="add-opportunity-btn">
                <Plus size={15} />
                Nouvelle opportunité
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingOpp ? 'Modifier l\'opportunité' : 'Nouvelle opportunité'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Titre *</Label>
                  <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required data-testid="opp-title" />
                </div>
                <div>
                  <Label>Contact *</Label>
                  <Select value={formData.contact_id} onValueChange={v => setFormData({ ...formData, contact_id: v })}>
                    <SelectTrigger data-testid="opp-contact"><SelectValue placeholder="Sélectionner un contact" /></SelectTrigger>
                    <SelectContent>
                      {Array.isArray(contacts) && contacts.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.company && ` · ${c.company}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valeur (€)</Label>
                    <Input type="number" min="0" step="100" value={formData.value} onChange={e => setFormData({ ...formData, value: e.target.value })} data-testid="opp-value" />
                  </div>
                  <div>
                    <Label>Probabilité (%)</Label>
                    <Input type="number" min="0" max="100" value={formData.probability} onChange={e => setFormData({ ...formData, probability: e.target.value })} data-testid="opp-probability" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Étape</Label>
                    <Select value={formData.stage} onValueChange={v => setFormData({ ...formData, stage: v })}>
                      <SelectTrigger data-testid="opp-stage"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_CONFIG[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Date de clôture</Label>
                    <Input type="date" value={formData.expected_close_date} onChange={e => setFormData({ ...formData, expected_close_date: e.target.value })} data-testid="opp-close-date" />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} data-testid="opp-description" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={saving || !formData.contact_id} data-testid="save-opp-btn">
                    {saving ? 'Enregistrement...' : (editingOpp ? 'Mettre à jour' : 'Créer')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Pipeline KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Valeur totale', value: formatCurrency(totalValue), sub: '+12% vs mois dernier', color: 'text-foreground' },
          { label: 'Deals actifs', value: activeDeals, sub: `Cycle moyen : 14 jours`, color: 'text-foreground' },
          { label: 'Conversion', value: `${opportunities.length > 0 ? Math.round((wonDeals.length / opportunities.length) * 100) : 0}%`, sub: 'Objectif : 30%', color: 'text-foreground' },
          { label: 'Clôturé gagné', value: formatCurrency(wonValue), sub: `${wonDeals.length} affaire(s)`, color: 'text-emerald-600 dark:text-emerald-400' },
        ].map((kpi, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{kpi.label}</p>
            <p className={`text-2xl font-bold leading-none ${kpi.color}`}>{kpi.value}</p>
            <p className="text-xs text-muted-foreground mt-1.5">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* ── Mobile list view ── */}
      <div className="md:hidden space-y-3">
        {/* Stage filter pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          <button
            onClick={() => setMobileStage('all')}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${mobileStage === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
          >
            Tous ({opportunities.length})
          </button>
          {STAGES.map(s => {
            const count = getOpportunitiesByStage(s).length;
            return (
              <button
                key={s}
                onClick={() => setMobileStage(s)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${mobileStage === s ? 'text-white' : 'bg-muted text-muted-foreground'}`}
                style={mobileStage === s ? { backgroundColor: STAGE_CONFIG[s].color } : {}}
              >
                {STAGE_CONFIG[s].label} ({count})
              </button>
            );
          })}
        </div>

        {/* Cards */}
        {opportunities
          .filter(o => mobileStage === 'all' || o.stage === mobileStage)
          .map(opp => {
            const cfg = STAGE_CONFIG[opp.stage];
            return (
              <div key={opp.id} className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="h-1" style={{ backgroundColor: cfg.color }} />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground text-sm leading-tight">{opp.title}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{getContactName(opp.contact_id)}</p>
                    </div>
                    <div className="flex gap-0.5 flex-shrink-0">
                      <button onClick={() => openEditDialog(opp)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDelete(opp.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <span className="text-lg font-bold text-foreground">{formatCurrency(opp.value)}</span>
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: `${cfg.color}20`, color: cfg.color }}
                    >
                      {cfg.label}
                    </span>
                  </div>

                  {opp.probability > 0 && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Confiance</span>
                        <span className="font-medium">{opp.probability}%</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${opp.probability}%`, backgroundColor: cfg.color }} />
                      </div>
                    </div>
                  )}

                  {opp.expected_close_date && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Calendar size={11} />
                      Clôture : {formatDate(opp.expected_close_date)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

        {opportunities.filter(o => mobileStage === 'all' || o.stage === mobileStage).length === 0 && (
          <div className="text-center py-10 text-muted-foreground text-sm">
            Aucune opportunité dans cette étape
          </div>
        )}

        <button
          onClick={() => openCreateDialog(mobileStage === 'all' ? 'lead' : mobileStage)}
          className="w-full py-3 rounded-xl border-2 border-dashed border-border text-sm text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors flex items-center justify-center gap-2"
        >
          <Plus size={16} />
          Nouvelle opportunité
        </button>
      </div>

      {/* ── Desktop Kanban Board ── */}
      <div className="hidden md:flex gap-3 overflow-x-auto" style={{ height: 'calc(100vh - 330px)' }} data-testid="kanban-board">
        {STAGES.map(stage => {
          const cfg = STAGE_CONFIG[stage];
          const stageOpps = getOpportunitiesByStage(stage);
          const stageValue = stageOpps.reduce((s, o) => s + Number(o.value || 0), 0);
          const isDragOver = dragOverStage === stage;

          return (
            <div
              key={stage}
              className={`flex-1 min-w-[160px] flex flex-col rounded-xl border border-border bg-card shadow-sm transition-all ${isDragOver ? 'ring-2 ring-primary/40 scale-[1.01]' : ''}`}
              onDragOver={e => handleDragOver(e, stage)}
              onDragLeave={() => setDragOverStage(null)}
              onDrop={e => handleDrop(e, stage)}
              data-testid={`kanban-column-${stage}`}
            >
              {/* Column top accent */}
              <div className={`h-1 rounded-t-xl`} style={{ backgroundColor: cfg.color }} />

              {/* Column Header */}
              <div className="p-3 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full truncate"
                      style={{ backgroundColor: `${cfg.color}20`, color: cfg.color }}
                    >
                      {cfg.label}
                    </span>
                    <span className="text-xs text-muted-foreground font-medium flex-shrink-0">{stageOpps.length}</span>
                  </div>
                  <MoreHorizontal size={14} className="text-muted-foreground flex-shrink-0 ml-1" />
                </div>
                {stageValue > 0 && (
                  <p className="text-sm font-bold text-foreground mt-1.5 truncate">{formatCurrency(stageValue)}</p>
                )}
              </div>

              {/* Cards */}
              <div className="flex-1 px-2 pb-2 space-y-2 overflow-y-auto min-h-0">
                {stageOpps.map(opp => (
                  <div
                    key={opp.id}
                    draggable
                    onDragStart={e => handleDragStart(e, opp)}
                    className="bg-background rounded-lg border border-border p-2.5 hover:shadow-md transition-all cursor-grab active:cursor-grabbing active:opacity-70 kanban-card"
                    data-testid={`opp-card-${opp.id}`}
                  >
                    {/* Card header */}
                    <div className="flex items-start justify-between gap-1 mb-1.5">
                      <h3 className="font-semibold text-xs text-foreground leading-tight">{truncate(opp.title, 28)}</h3>
                      <div className="flex gap-0.5 flex-shrink-0">
                        <button
                          onClick={() => openEditDialog(opp)}
                          className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          data-testid={`edit-opp-${opp.id}`}
                        >
                          <Pencil size={11} />
                        </button>
                        <button
                          onClick={() => handleDelete(opp.id)}
                          className="p-0.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                          data-testid={`delete-opp-${opp.id}`}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>

                    {/* Contact */}
                    <p className="text-[10px] text-muted-foreground mb-2 truncate">{getContactName(opp.contact_id)}</p>

                    {/* Probability bar */}
                    {opp.probability > 0 && (
                      <div className="mb-2">
                        <div className="flex justify-between text-[10px] mb-0.5">
                          <span className="text-muted-foreground">Confiance</span>
                          <span className="font-medium text-foreground">{opp.probability}%</span>
                        </div>
                        <div className="h-1 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${opp.probability}%`, backgroundColor: cfg.color }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="font-bold text-foreground text-xs">{formatCurrency(opp.value)}</span>
                      {opp.expected_close_date && (
                        <span className="flex items-center gap-0.5 text-muted-foreground">
                          <Calendar size={10} />
                          {formatDate(opp.expected_close_date)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}

                {stageOpps.length === 0 && (
                  <button
                    onClick={() => openCreateDialog(stage)}
                    className="w-full py-4 rounded-lg border-2 border-dashed border-border text-[10px] text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors flex items-center justify-center gap-1"
                  >
                    <Plus size={12} />
                    Nouveau
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
